import { inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { updateThemeMeta } from '@shared/utilities/update-theme-meta';
import { IThemeStore, ThemeMode } from '@shared/interfaces/IThemeStore';

const getSystemPrefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveIsDarkMode = (mode: ThemeMode): boolean =>
  mode === 'system' ? getSystemPrefersDark() : mode === 'dark';

const initialState: IThemeStore = {
  mode: 'system',
  isDarkMode: getSystemPrefersDark(),
};

export const ThemeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({
    key: 'theme-store',
    select: ({ mode }) => ({ mode }),
  }),
  withMethods((store, metaService = inject(Meta)) => {
    let initialized = false;
    let mediaQuery: MediaQueryList | null = null;

    const applyTheme = (mode: ThemeMode) => {
      const isDarkMode = resolveIsDarkMode(mode);

      updateThemeMeta(metaService, isDarkMode ? 'dark' : 'light');
      patchState(store, { mode, isDarkMode });
    };

    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      if (store.mode() !== 'system') return;

      updateThemeMeta(metaService, event.matches ? 'dark' : 'light');
      patchState(store, { isDarkMode: event.matches });
    };

    return {
      init: () => {
        if (initialized) return;
        initialized = true;

        applyTheme(store.mode());

        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
          mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          mediaQuery.addEventListener('change', handleSystemThemeChange);
        }
      },

      setThemeMode: (mode: ThemeMode) => {
        applyTheme(mode);
      },

      updateIsDarkmode: (isDarkMode: boolean) => {
        applyTheme(isDarkMode ? 'dark' : 'light');
      },

      toggleTheme: () => {
        applyTheme(store.isDarkMode() ? 'light' : 'dark');
      },
    };
  }),
);
